'use client';

import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon } from '@/assets/icons/CloseIcon';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { Text } from '@/ui-components/atoms/Text/Text';
import styles from './bottomSheet.module.scss';

export interface BottomSheetProps {
    id?: string;
    title: string;
    closeLabel?: string;
    children: ReactNode;
    onClose: () => void;
}

export function BottomSheet({ id, title, closeLabel = 'Close', children, onClose }: BottomSheetProps) {
    const titleId = useId();
    const dialogRef = useRef<HTMLDialogElement>(null);
    useBodyScrollLock(true);

    useEffect(() => {
        const dialog = dialogRef.current;
        const trigger = document.activeElement as HTMLElement | null;
        dialog?.showModal();
        return () => {
            dialog?.close();
            trigger?.focus({ preventScroll: true });
        };
    }, []);

    return createPortal(
        <dialog
            id={id}
            ref={dialogRef}
            aria-labelledby={titleId}
            className={styles.sheet}
            onCancel={(event) => {
                event.preventDefault();
                onClose();
            }}
            onClickCapture={(event) => {
                if (event.target === event.currentTarget) {
                    onClose();
                }
            }}
        >
            <div className={styles.panel}>
                <div className={styles.header}>
                    <button type="button" className={styles.close} onClick={onClose} aria-label={closeLabel}>
                        <CloseIcon />
                    </button>
                    <Text tag="h2" id={titleId} className={styles.title}>
                        {title}
                    </Text>
                </div>
                <div className={styles.content}>{children}</div>
            </div>
        </dialog>,
        document.body,
    );
}
